import {
  ArrowRight,
  BookOpenText,
  Building2,
  Calculator,
  ContactRound,
  Files,
  Gavel,
  Library,
  Search,
  ScanSearch,
  Scale,
} from "lucide-react";
import { AppLink, PageHeader } from "@tribunalea/ui";
import { canAccessTool, TOOL_IDS } from "@tribunalea/shared";

const tools = [
  {
    id: "case-search",
    accessId: TOOL_IDS.INSTRUMENTA_EXPEDIENTES,
    title: "Expedientes",
    description:
      "Consultá expedientes judiciales por número o carátula sin agregarlos ni activar seguimientos.",
    icon: Search,
    to: "/expedientes",
    capabilities: [
      "Consulta en fuentes judiciales conectadas",
      "Información y actuaciones del expediente",
      "Acceso a documentos judiciales",
    ],
  },
  {
    id: "norm-catalog",
    accessId: TOOL_IDS.INSTRUMENTA_NORMS,
    title: "Normas",
    description:
      "Buscá normativa por nombre, número o contenido y consultá el texto aplicable a una fecha.",
    icon: Scale,
    to: "/normas",
    capabilities: [
      "Filtros por jurisdicción, tipo y estado",
      "Consulta de vigencia histórica",
      "Fichas permanentes por norma",
    ],
  },
  {
    id: "jurisprudence-search",
    accessId: TOOL_IDS.INSTRUMENTA_JURISPRUDENCE,
    title: "Jurisprudencia",
    description:
      "Encontrá criterios y situaciones análogas dentro del texto de sentencias oficiales de Mendoza.",
    icon: ScanSearch,
    to: "/jurisprudencia",
    capabilities: [
      "Búsqueda textual y semántica",
      "Pasajes relevantes con fuente oficial",
      "Análisis y comparación con Justiniano",
    ],
  },
  {
    id: "doctrine-catalog",
    accessId: TOOL_IDS.INSTRUMENTA_DOCTRINE,
    title: "Doctrina",
    description:
      "Recorré obras doctrinarias y encontrá autores, títulos o conceptos dentro de su contenido.",
    icon: BookOpenText,
    to: "/doctrina",
    capabilities: [
      "Navegación del catálogo completo",
      "Un campo para autor, título y contenido",
      "Fichas permanentes por obra",
    ],
  },
  {
    id: "library",
    accessId: TOOL_IDS.INSTRUMENTA_LIBRARY,
    title: "Biblioteca IA",
    description:
      "Organizá PDFs en cuadernos, consultalos con Justiniano y verificá cada respuesta en el documento original.",
    icon: Library,
    to: "/biblioteca",
    capabilities: [
      "Cuadernos por espacio",
      "Extracción, OCR y búsqueda semántica",
      "Respuestas con referencias",
    ],
  },
  {
    id: "document-processor",
    accessId: TOOL_IDS.INSTRUMENTA_DOCUMENT_PROCESSOR,
    title: "Procesador documental",
    description:
      "Extraé datos de uno o más PDFs, revisá la evidencia y generá resultados con plantillas reutilizables.",
    icon: Files,
    to: "/procesador-documental",
    capabilities: [
      "Extracción de información",
      "Plantillas reutilizables",
      "Revisión y exportación",
    ],
  },
  {
    id: "judicial-interest",
    accessId: TOOL_IDS.INSTRUMENTA_JUDICIAL_INTEREST,
    title: "Calculadora de intereses",
    description:
      "Actualizá capitales con las tasas aplicables en Mendoza y revisá el detalle de cada período calculado.",
    icon: Calculator,
    to: "/calculadora-intereses",
    capabilities: [
      "Cálculo manual y automático",
      "Aplicación por tramos legales",
      "Detalle de días, tasas y porcentajes",
    ],
  },
  {
    id: "tax-identity-search",
    accessId: TOOL_IDS.INSTRUMENTA_TAX_ID,
    title: "Buscador de CUIT",
    description:
      "Encontrá el CUIT de una sociedad por su nombre o consultá qué razón social corresponde a un CUIT.",
    icon: Building2,
    to: "/consulta-fiscal",
    capabilities: [
      "Búsqueda por razón social",
      "Consulta por CUIT",
      "Fuente oficial identificada",
    ],
  },
  {
    id: "professional-search",
    accessId: TOOL_IDS.INSTRUMENTA_PROFESSIONALS,
    title: "Matrículas",
    description:
      "Encontrá profesionales por nombre, apellido o matrícula y verificá la información en el padrón correspondiente.",
    icon: ContactRound,
    to: "/matriculas-profesionales",
    capabilities: [
      "Búsqueda por nombre o matrícula",
      "Jurisdicción claramente identificada",
      "Acceso al padrón de origen",
    ],
  },
  {
    id: "sentence-search",
    accessId: TOOL_IDS.INSTRUMENTA_SENTENCES,
    title: "Sentencias",
    description:
      "Encontrá expedientes con sentencia en el Poder Judicial de Mendoza y abrí el documento original.",
    icon: Gavel,
    to: "/buscador-sentencias",
    capabilities: [
      "Búsqueda por carátula, CUIJ o número",
      "Resultados paginados de PJM",
      "Acceso al PDF de cada sentencia",
    ],
  },
];

const toolSections = [
  {
    id: "information",
    label: "Información",
    toolIds: [
      "case-search",
      "sentence-search",
      "norm-catalog",
      "jurisprudence-search",
      "doctrine-catalog",
    ],
  },
  {
    id: "tools",
    label: "Herramientas",
    toolIds: [
      "judicial-interest",
      "document-processor",
      "library",
      "tax-identity-search",
      "professional-search",
    ],
  },
];

const toolsById = new Map(tools.map((tool) => [tool.id, tool]));

export function ToolsPage({ access }) {
  const visibleSections = toolSections.map((section) => ({
    ...section,
    tools: section.toolIds
      .map((toolId) => toolsById.get(toolId))
      .filter((tool) => tool && canAccessTool(access, tool.accessId)),
  })).filter((section) => section.tools.length);

  return (
    <div className="page tools-page">
      <PageHeader
        hero
        eyebrow="Tribunalea · Instrumenta"
        title="Herramientas para trabajar con información."
        description="Elegí una herramienta para procesar documentos o consultar datos. Vamos a sumar nuevas utilidades a este espacio."
      />

      <div className="tools-sections">
        {visibleSections.map((section) => (
          <section
            className="tools-section"
            aria-labelledby={`tools-section-${section.id}`}
            key={section.id}
          >
            <div className="tools-section-heading">
              <h2 id={`tools-section-${section.id}`}>{section.label}</h2>
            </div>
            <div className="tools-grid">
              {section.tools.map((tool) => {
                const Icon = tool.icon;
                return (
                  <AppLink className="tool-card" key={tool.id} to={tool.to}>
                    <div className="tool-card-heading">
                      <span className="tool-card-icon">
                        <Icon size={25} />
                      </span>
                      <h3>{tool.title}</h3>
                    </div>
                    <p>{tool.description}</p>
                    <ul>
                      {tool.capabilities.map((capability) => (
                        <li key={capability}>
                          <ScanSearch size={15} />
                          {capability}
                        </li>
                      ))}
                    </ul>
                    <span className="primary-button tool-card-action">
                      Abrir herramienta
                      <ArrowRight size={17} />
                    </span>
                  </AppLink>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
